// Reordering. Two controls, one idea: a list is never ordered by a typed number.
//   - Drag (Spencer, 2026-10-07): a grip (⋮⋮) on every row of a list worked on at a desk, the
//     Library and Settings. Drag the grip, drop the row where it goes; the list renumbers 1..n.
//   - Arrows: up and down for a list worked on a phone or tablet (a job's rooms).
// Both keep the sort column, which the unverify trigger ignores, so a reorder is not an edit.

import { useState, type DragEvent, type ReactNode } from 'react';
import { updateRow, type Row } from '../lib/api';
import { Btn } from './kit';

/** Renumber a list 1..n in a new order, writing only the rows whose sort changed. */
async function renumber(table: string, ordered: Row[]) {
  for (const [n, r] of ordered.entries()) if (r.sort !== n + 1) await updateRow(table, r.id, { sort: n + 1 });
}

/** Move one row up or down within its list. */
export async function reorder(table: string, rows: Row[], id: string, dir: -1 | 1) {
  const ordered = [...rows];
  const i = ordered.findIndex((r) => r.id === id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= ordered.length) return;
  [ordered[i], ordered[j]] = [ordered[j]!, ordered[i]!];
  await renumber(table, ordered);
}

/** Move one row to where another sits: the dragged row takes the target's place, the rest close up. */
export async function moveTo(table: string, rows: Row[], id: string, targetId: string) {
  const ordered = [...rows];
  const from = ordered.findIndex((r) => r.id === id);
  const to = ordered.findIndex((r) => r.id === targetId);
  if (from < 0 || to < 0 || from === to) return;
  const [row] = ordered.splice(from, 1);
  ordered.splice(to, 0, row!);
  await renumber(table, ordered);
}

/** The sort a new row takes so it lands at the bottom of its list. */
export function nextSort(rows: Row[]): number {
  return rows.reduce((m, r) => Math.max(m, Number(r.sort) || 0), 0) + 1;
}

// ---------- drag ----------

export interface DragHandles {
  /** Props for the grip element of a row: the thing a person drags. */
  grip: (id: string) => Record<string, unknown>;
  /** Props for the row element itself: where a dragged row can be dropped. */
  /** base: the element's own classes, kept while a row is dragged over it (spreading these after className would otherwise drop them). */
  row: (id: string, base?: string) => { onDragOver: (e: DragEvent) => void; onDragLeave: () => void; onDrop: (e: DragEvent) => void; className?: string };
  /** The id being dragged, if any. */
  dragging: string | null;
}

/**
 * Drag to reorder over a list of ids. `onDrop(id, targetId)` puts the dragged one where the
 * target sits. The grip is the draggable element; the whole row (a table row, a line) is
 * the drop target, and shows a line where the row will land.
 */
export function useDragOrder(onDrop: (id: string, targetId: string) => void | Promise<unknown>, disabled?: boolean): DragHandles {
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  return {
    dragging,
    grip: (id) =>
      disabled
        ? { className: 'grip off', title: '' }
        : {
            className: `grip${dragging === id ? ' lifting' : ''}`,
            title: 'Drag to reorder',
            draggable: true,
            onDragStart: (e: DragEvent<HTMLElement>) => {
              setDragging(id);
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', id);
              // the whole row is what moves, not the grip alone
              const rowEl = e.currentTarget.closest('tr, .ln, .dragrow') as HTMLElement | null;
              if (rowEl) e.dataTransfer.setDragImage(rowEl, 12, 12);
            },
            onDragEnd: () => {
              setDragging(null);
              setOver(null);
            },
          },
    row: (id, base) => ({
      onDragOver: (e) => {
        if (!dragging || dragging === id) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (over !== id) setOver(id);
      },
      onDragLeave: () => {
        if (over === id) setOver(null);
      },
      onDrop: (e) => {
        e.preventDefault();
        const id0 = dragging ?? e.dataTransfer.getData('text/plain');
        setDragging(null);
        setOver(null);
        if (id0 && id0 !== id) void onDrop(id0, id);
      },
      ...(() => {
        const cls = [base, over === id && dragging && dragging !== id ? 'drop-here' : ''].filter(Boolean).join(' ');
        return cls ? { className: cls } : {};
      })(),
    }),
  };
}

/** Drag to reorder the rows of a table kept in order by `sort`. */
export function useDragRows(table: string, rows: Row[], disabled?: boolean): DragHandles {
  return useDragOrder((id, target) => moveTo(table, rows, id, target), disabled);
}

/** The grip itself. Spread the handles' `grip(id)` onto it. */
export function Grip(props: Record<string, unknown>): ReactNode {
  return (
    <span aria-label="Drag to reorder" {...props}>
      ⋮⋮
    </span>
  );
}

// ---------- arrows ----------

/** Up and down for one row of a list kept in a table (a job's rooms: a phone screen). */
export function Move({ table, rows, id, disabled }: { table: string; rows: Row[]; id: string; disabled?: boolean }) {
  const i = rows.findIndex((r) => r.id === id);
  return <Arrows up={i > 0} down={i >= 0 && i < rows.length - 1} disabled={disabled} onMove={(dir) => reorder(table, rows, id, dir)} />;
}

/** The two arrows themselves. */
export function Arrows({ up, down, disabled, onMove }: { up: boolean; down: boolean; disabled?: boolean; onMove: (dir: -1 | 1) => void | Promise<unknown> }) {
  return (
    <span className="nowrap">
      <Btn small disabled={disabled || !up} title="Move up" onClick={(e) => { e.stopPropagation(); void onMove(-1); }}>
        ↑
      </Btn>{' '}
      <Btn small disabled={disabled || !down} title="Move down" onClick={(e) => { e.stopPropagation(); void onMove(1); }}>
        ↓
      </Btn>
    </span>
  );
}
